import { Mic } from "lucide-react";
import { useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useSource } from "@/hooks/use-sources";
import { cn } from "@/lib/utils";

interface TranscriptContextSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The source the quote came from. */
  sourceId: string | null;
  /** The quote text to highlight in the transcript. */
  quote: string;
  /** Optional surrounding-context blob captured at extraction time. */
  quoteContext: string | null;
}

const CONTEXT_LINES = 3;

export function TranscriptContextSheet({
  open,
  onOpenChange,
  sourceId,
  quote,
  quoteContext,
}: TranscriptContextSheetProps) {
  const { data, isLoading } = useSource(sourceId);

  const slice = useMemo(() => {
    if (!data?.transcriptLines || data.transcriptLines.length === 0) return null;
    const idx = data.transcriptLines.findIndex((l) => lineMatches(l.text, quote, l.highlighted));
    if (idx < 0) {
      // No exact match — show the first 8 lines as a sensible fallback.
      return {
        before: [],
        match: null as null,
        after: data.transcriptLines.slice(0, 8),
        matchedIdx: -1,
      };
    }
    const before = data.transcriptLines.slice(Math.max(0, idx - CONTEXT_LINES), idx);
    const match = data.transcriptLines[idx];
    const after = data.transcriptLines.slice(idx + 1, idx + 1 + CONTEXT_LINES);
    return { before, match, after, matchedIdx: idx };
  }, [data, quote]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[600px] flex flex-col">
        <SheetHeader className="border-b border-[var(--color-border)]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold inline-flex items-center gap-1.5">
            <Mic className="h-3 w-3" />
            Transcript context
          </div>
          {data && (
            <SheetTitle className="font-display text-base leading-snug mt-1">
              {data.name}
            </SheetTitle>
          )}
          <SheetDescription className="text-xs mt-1">
            Showing the quote with {CONTEXT_LINES} line{CONTEXT_LINES === 1 ? "" : "s"} before and
            after for context.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {isLoading && (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/6" />
            </div>
          )}

          {data && !slice && <FallbackContext quote={quote} quoteContext={quoteContext} />}

          {data && slice && slice.match === null && (
            <>
              <div className="rounded-md border-l-2 border-warning bg-warning-bg/40 px-3 py-2 text-xs text-foreground/80">
                Couldn't pinpoint the exact line in the transcript — showing the start of the source
                instead.
              </div>
              <FallbackContext quote={quote} quoteContext={quoteContext} />
              <ol className="space-y-2 text-sm">
                {slice.after.map((line) => (
                  <Line key={line.id} line={line} highlighted={false} />
                ))}
              </ol>
            </>
          )}

          {data && slice && slice.match && (
            <ol className="space-y-2 text-sm">
              {slice.before.map((line) => (
                <Line key={line.id} line={line} highlighted={false} />
              ))}
              <Line line={slice.match} highlighted />
              {slice.after.map((line) => (
                <Line key={line.id} line={line} highlighted={false} />
              ))}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Line({
  line,
  highlighted,
}: {
  line: { id: string; tsLabel: string | null; text: string; stakeholderId: string | null };
  highlighted: boolean;
}) {
  return (
    <li
      className={cn(
        "flex gap-3 rounded-md px-3 py-2",
        highlighted ? "border-l-2 border-nymbl-blue bg-nymbl-sky/40" : "text-foreground/75",
      )}
    >
      {line.tsLabel && (
        <span
          className={cn(
            "text-[11px] tabular-nums shrink-0 w-14 mt-0.5 font-mono",
            highlighted ? "text-nymbl-navy" : "text-muted-foreground",
          )}
        >
          {line.tsLabel}
        </span>
      )}
      <span
        className={cn("leading-relaxed", highlighted ? "text-foreground font-medium italic" : "")}
      >
        {highlighted ? `“${line.text}”` : line.text}
      </span>
    </li>
  );
}

function FallbackContext({ quote, quoteContext }: { quote: string; quoteContext: string | null }) {
  return (
    <div className="space-y-3">
      <blockquote className="rounded-md border-l-2 border-nymbl-blue bg-nymbl-sky/40 px-4 py-3 text-sm italic text-foreground/85">
        “{quote}”
      </blockquote>
      {quoteContext && (
        <p className="text-xs text-muted-foreground whitespace-pre-wrap">{quoteContext}</p>
      )}
      {!quoteContext && (
        <p className="text-xs text-muted-foreground">
          Full transcript not yet imported. The AI extracted only the quote and the immediate
          context.
        </p>
      )}
    </div>
  );
}

function lineMatches(text: string, quote: string, highlighted: string | null): boolean {
  if (highlighted && highlighted.length > 8 && quote.includes(highlighted)) return true;
  if (text === quote) return true;
  // Fuzzy: substring match in either direction (the AI may have trimmed).
  const minLen = 16;
  if (quote.length >= minLen && text.includes(quote.slice(0, minLen))) return true;
  if (text.length >= minLen && quote.includes(text.slice(0, minLen))) return true;
  return false;
}
